import { useAlertDialog } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";
import { useRenderManager } from "../useRenderManager";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { TileRenderState } from "../RenderState/common";
import { AppType } from "client/Api/Apps/common";
import { useBaseNavigation } from "./useInvokeNavigation";
import { TileContextProvider } from "../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type UnknownTileContentProps = {
	renderState: TileRenderState<AppType.Unknown>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const UnknownTileContent = (props: UnknownTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useUnknownTileNavigation(renderState, onEdit);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<TwoByTwoTileContent
				onClick={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
UnknownTileContent.displayName = "UnknownTileContent";

export function useUnknownTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Unknown>, "data" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const [alertDialog, openAlertDialog] = useAlertDialog();

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		[
			alertDialog,
			useCallback(
				(): void => {
					openAlertDialog({
						title: localize("_NotSupported"),
						children: localize("_UnknownTileMessage"),
						confirmText: localize("_Close"),
						trackingId: "unknown-tile-dialog"
					});
				},
				[openAlertDialog]
			)
		]
	);
}
